import _, { isEmpty, orderBy } from 'lodash';
import { ltrim } from 'underscore.string';
import Debug from 'debug';
import routes from '../../services/routes';
import { isClient } from '../../app/globals';
import { logCriticalError } from '@joybird/joystagram-components/dist/esm/logger';
import isLighthouseTestRun from '@joybird/joystagram-components/dist/esm/isLighthouseTestRun';
import usePostHogFeatureFlag from '@joybird/joystagram-components/dist/esm/usePostHogFeatureFlag';
import { getFinalParamsForDynamicVariants } from '../../commons/utils';
const debug = Debug('consumer:session');

class SessionUser {
  constructor(user, userAudiences = []) {
    this.user = user;
    this.audiences = userAudiences;
  }

  audienceUpdateHandler(message) {
    this.audiences = message
      .filter(audience => audience.value)
      .map(({ _id: id, rank }) => ({ id, rank }));
  }

  get user() {
    return this._user;
  }

  set user(value) {
    this._user = Object.freeze(value);
  }

  get audiences() {
    return this._audiences;
  }

  set audiences(value) {
    if (!value) {
      value = [];
    }
    value = [...value, { id: '0', name: 'Default', rank: 100000 }];
    this._audienceDetails = Object.freeze(orderBy(value, ['rank', 'desc']));

    this._audiences = Object.freeze(
      this.audienceDetails.map(audience => audience.id)
    );
    debug("User's audience(s) have been set.", this.audienceDetails);
  }

  get audienceDetails() {
    return this._audienceDetails;
  }

  calculateRouteVariant({ srcPath, params, query, serverRouteParams }) {
    const rebrandFlag = usePostHogFeatureFlag('homepageRebrand2023');
    srcPath = ltrim(srcPath, '/');

    debug('Selecting route variant.', {
      srcPath: srcPath,
      params: params,
      query: query,
      'sessionUser.audienceDetails': this.audienceDetails,
    });

    const route = routes.getRouteByPath(srcPath || '/');

    debug('Route located.', route);

    if (!route) {
      const err = new Error('Unable to locate route by slug.');
      err.srcPath = srcPath;
      err.params = params;
      logCriticalError(err);
      throw err;
    }

    const audienceOverride = query['audience-id']
      ? parseInt(query['audience-id'])
      : undefined;
    const routeHasDynamicVariants =
      route.type === 'Dynamic' && !!route.dynamicVariants?.length;
    let dynamicVariantMatch = null;
    let finalParams = params;
    if (routeHasDynamicVariants && !isClient() && !isEmpty(serverRouteParams)) {
      // server route params contains full path, pick key(s) from it
      finalParams = getFinalParamsForDynamicVariants({
        srcPath,
        serverRouteParams,
      });
    }
    // Check if dynamic variant matches param
    if (!isEmpty(finalParams) && routeHasDynamicVariants) {
      // Get dynamic key, join params if multiple available
      const paramKeys = Object.keys(finalParams);
      let paramKey =
        paramKeys.length > 1
          ? paramKeys.map(pKey => finalParams[pKey]).join('/')
          : finalParams[paramKeys[0]];

      if (paramKey.endsWith('/')) {
        paramKey = paramKey.slice(0, -1);
      }
      const { dynamicVariants } = route;
      dynamicVariantMatch = dynamicVariants.find(dynVarnt => {
        const finalDynVariantKey = dynVarnt.dynamicKey.endsWith('/')
          ? dynVarnt.dynamicKey.slice(0, -1)
          : dynVarnt.dynamicKey;
        return finalDynVariantKey.trim() === paramKey.trim();
      });
    }
    let variant;

    if (dynamicVariantMatch?.variant) {
      variant = dynamicVariantMatch.variant;
    } else if (audienceOverride) {
      // TODO: Apply this to dynamic variants as well once activated
      variant = _.find(route.variants, {
        audience_id: audienceOverride,
      });
      if (!variant) {
        const err = new Error(
          `Unable to locate route variant matching provided override ID: ${audienceOverride}`
        );
        err.audienceOverride = audienceOverride;
        err.route = route;
        throw err;
      }
    } else if (route.defaultVariantId) {
      const routeDefVariant = _.find(route.variants, {
        id: route.defaultVariantId,
      });
      if (routeDefVariant) {
        if (rebrandFlag === 'test' && route?.title?.toLowerCase() === 'home') {
          variant =
            route?.variants?.find(
              variant => variant.name.toLowerCase() === 'oct 2023'
            ) ?? routeDefVariant;
        } else variant = routeDefVariant;
        variant = routeDefVariant;
      }
    } else {
      for (const audienceID of this.audiences) {
        const routeVariant = _.find(route.variants, {
          audience_id: audienceID,
        });
        if (routeVariant) {
          variant = routeVariant;
        }
      }
    }

    // ref: https://stitchindustries.atlassian.net/browse/CN-284
    const search = isClient()
      ? new URLSearchParams(window.location.search)
      : params;
    const variantOverrideId = isClient()
      ? search.get('variantId')
      : search.variantId;
    if (variantOverrideId) {
      const variantOverride = _.find(route.variants, {
        id: variantOverrideId,
      });
      if (variantOverride) {
        variant = variantOverride;
      }
    }

    variant = _.cloneDeep(variant);
    if (isClient() && variant?.id) {
      const ssrVersion = document.querySelector('meta[name="ssr-version"]')
        ?.content;
      if (ssrVersion?.search(':') === -1) {
        document.querySelector(
          'meta[name="ssr-version"]'
        ).content = `${ssrVersion}:${variant?.id}:${variant?.name}`;
      }
    }
    variant.layout = _.find(routes.layouts, {
      id: variant.layout_id,
    });

    debug('Variant selected.', variant);

    return variant;
  }
}

export default SessionUser;
